/* eslint-disable no-console -- this module is the one sanctioned console wrapper */
import { ENV } from "../config/env";

/**
 * Centralized leveled logger.
 *
 * - `debug` / `info` / `log` are gated by {@link ENV.DEBUG} — they no-op in
 *   staging/production builds and only print in development. Use these for
 *   breadcrumbs and any payload that may contain PII or backend identifiers,
 *   so that data never reaches a deployed user's console.
 * - `warn` / `error` always pass through (kept in production by the Vite
 *   `esbuild.pure` config) — they carry real failure signal.
 * - `scope(prefix)` returns a logger that prepends `prefix` to every message,
 *   replacing the ad-hoc `[Name]` / emoji prefixes scattered through the app.
 * - `warnOnce(key, …)` warns at most once per `key` for the process lifetime —
 *   for validations that would otherwise fire on every render or API response.
 */

type LogArgs = unknown[];
type ConsoleFn = (...args: LogArgs) => void;

export interface Logger {
  debug: ConsoleFn;
  info: ConsoleFn;
  log: ConsoleFn;
  warn: ConsoleFn;
  error: ConsoleFn;
  /** Returns a child logger that prepends `prefix` to every message. */
  scope: (prefix: string) => Logger;
  /** Warns at most once per `key` (module-lifetime dedup). Always passes through. */
  warnOnce: (key: string, ...args: LogArgs) => void;
}

const debugEnabled = ENV.DEBUG;
const warnedKeys = new Set<string>();

function makeLogger(prefix?: string): Logger {
  // `gated` levels are silenced unless debug is on; ungated levels always fire.
  const wrap =
    (fn: ConsoleFn, gated: boolean): ConsoleFn =>
    (...args: LogArgs) => {
      if (gated && !debugEnabled) return;
      if (prefix) fn(prefix, ...args);
      else fn(...args);
    };

  return {
    debug: wrap(console.debug.bind(console), true),
    info: wrap(console.info.bind(console), true),
    log: wrap(console.log.bind(console), true),
    warn: wrap(console.warn.bind(console), false),
    error: wrap(console.error.bind(console), false),
    scope: (childPrefix: string) =>
      makeLogger(prefix ? `${prefix} ${childPrefix}` : childPrefix),
    warnOnce: (key: string, ...args: LogArgs) => {
      if (warnedKeys.has(key)) return;
      warnedKeys.add(key);
      if (prefix) console.warn(prefix, ...args);
      else console.warn(...args);
    },
  };
}

export const logger = makeLogger();

export default logger;
